import { useContext, useEffect } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import {
  Alert,
  AlertIcon,
  Box,
  AlertDescription,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';

import TextFieldInput from 'components/fields/auto/TextFieldInput';
import FormInput from 'components/fields/auto/FormInput';
import DateInput from 'components/fields/auto/DateInput';
import RadioInput from 'components/fields/auto/RadioInput';

import getDateFromString from 'helpers/getDateFromString';
import getRadioYesNo from 'helpers/getRadioYesNo';

import getDateString from 'helpers/getDateString';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import {
  changeUnlistedDriver,
  addOrUpdateRemark,
  removeMissingMandatoryField,
} from 'reducers/autoPersonalFormReducer';

import { SECTIONS } from 'views/auto_form/constants';

const UnlistedDrivers = () => {
  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };
  const dispatch = useAppDispatch();

  // Color mode values for section headers
  const sectionBg = useColorModeValue('#ccc', 'gray.600');
  const sectionColor = useColorModeValue('black', 'white');

  const drivers = useAppSelector(
    (state) => state.autoPersonalForm.data.unlisted_drivers,
  );

  const validation = validations.find((v) => v.validation_id === '026');

  useEffect(() => {
    drivers.forEach((driver, index) => {
      if (!driver.is_in_household) {
        dispatch(
          addOrUpdateRemark({
            validation_key: '026',
            item_key: `unlisted-driver-${index + 1}`,
            data: {
              text: '',
            },
          }),
        );
      } else {
        dispatch(
          addOrUpdateRemark({
            validation_key: '026',
            item_key: `unlisted-driver-${index + 1}`,
            data: {
              text: `Driver: ${
                driver.name
              } - Is this driver still in the household?: Yes - License: ${
                driver.license_number
              } - Carrier: ${driver.carrier || 'N/A'} - Policy #: ${
                driver.policy || 'N/A'
              } - Expiry Date: ${
                driver.expiry_date || 'N/A'
              } - Unlicensed Reason: ${
                driver.unlicensed_reason || 'N/A'
              } - Remarks: ${driver.remarks || 'N/A'}`,
            },
          }),
        );
      }
    });
  }, [drivers, dispatch]);

  useEffect(() => {
    drivers.forEach((driver) => {
      if (!driver.is_in_household) {
        dispatch(removeMissingMandatoryField(`License (${driver.name})`));
        dispatch(removeMissingMandatoryField(`Carrier (${driver.name})`));
        dispatch(removeMissingMandatoryField(`Policy # (${driver.name})`));
        dispatch(removeMissingMandatoryField(`Expiry Date (${driver.name})`));
      }
    });
  }, [drivers, dispatch]);

  const onToggleChange = (index: number, status: boolean) => {
    dispatch(
      changeUnlistedDriver({ name: 'is_in_household', value: status, index }),
    );
  };

  const onDriverChange = (event: any, index: number) => {
    const { name, value } = event.target;

    dispatch(changeUnlistedDriver({ name, value, index }));
  };

  const onDateChange = (date: Date, name: string, index: number) => {
    dispatch(changeUnlistedDriver({ name, index, value: getDateString(date) }));
  };

  if (!validation || !drivers) return null;

  return (
    <Box mt="20px">
      <Alert status="warning" mb="20px" fontSize="14px">
        <AlertIcon />
        <Box w="100%">
          <AlertDescription>
            <ValidationMessage message={validation.validation_message} />
          </AlertDescription>
        </Box>
      </Alert>

      <Box pl="20px">
        {drivers.map((driver, index: number) => {
          return (
            <Box mt={index !== 0 ? '20px' : ''} key={index}>
              <Box
                backgroundColor={sectionBg}
                color={sectionColor}
                rounded="5px"
                p="5px"
                pl="10px"
              >
                <strong>({index + 1}) Driver: </strong> {driver.name}
              </Box>

              <Box mt="10px" mb="10px">
                <RadioInput
                  label="Is this driver still in the household?"
                  subLabel={driver.name}
                  section={SECTIONS.DRIVERS}
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                  value={getRadioYesNo(driver.is_in_household)}
                  onChange={(value) => onToggleChange(index, value === 'true')}
                  required
                />
              </Box>

              {driver.is_in_household && (
                <Box>
                  <Flex gap="20px">
                    <FormInput
                      section={SECTIONS.DRIVERS}
                      label="License"
                      subLabel={driver.name}
                      name="license_number"
                      value={driver.license_number}
                      onChange={(e) => onDriverChange(e, index)}
                      required={driver.unlicensed_reason.length === 0}
                    />
                    <FormInput
                      section={SECTIONS.DRIVERS}
                      label="Carrier"
                      subLabel={driver.name}
                      name="carrier"
                      value={driver.carrier}
                      onChange={(e) => onDriverChange(e, index)}
                      required={driver.unlicensed_reason.length === 0}
                    />
                    <FormInput
                      section={SECTIONS.DRIVERS}
                      label="Policy #"
                      subLabel={driver.name}
                      name="policy"
                      value={driver.policy}
                      onChange={(e) => onDriverChange(e, index)}
                      required={driver.unlicensed_reason.length === 0}
                    />
                    <DateInput
                      section={SECTIONS.DRIVERS}
                      label="Expiry Date"
                      subLabel={driver.name}
                      date={getDateFromString(driver.expiry_date)}
                      onChange={(date: Date) =>
                        onDateChange(date, 'expiry_date', index)
                      }
                      required={driver.unlicensed_reason.length === 0}
                    />
                  </Flex>

                  <Flex gap="20px" mt="10px">
                    <Box w="40%">
                      <TextFieldInput
                        section={SECTIONS.DRIVERS}
                        label="If driver is unlicensed, give explanation"
                        name="unlicensed_reason"
                        value={driver.unlicensed_reason}
                        onChange={(e: Event) => onDriverChange(e, index)}
                      />
                    </Box>

                    <Box w="60%">
                      <TextFieldInput
                        section={SECTIONS.DRIVERS}
                        label="Remarks"
                        name="remarks"
                        value={driver.remarks}
                        onChange={(e: Event) => onDriverChange(e, index)}
                      />
                    </Box>
                  </Flex>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default UnlistedDrivers;
